import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useT } from '@/lib/i18n/useT';
import type { XkStationSummary } from '@/lib/api/types';
import { ResponsesOverTimeChart } from './charts/ResponsesOverTimeChart';
import { StockWonChart } from './charts/StockWonChart';
import { InventoryDonut } from './charts/InventoryDonut';
import { TopAnswersChart } from './charts/TopAnswersChart';

interface DashboardTabProps {
  summary: XkStationSummary;
}

export function DashboardTab({ summary }: DashboardTabProps) {
  const t = useT();
  const hasResponses = summary.responses.totalResponses > 0;
  const hasInventory = summary.inventoryTotals.quantity > 0;

  // If both inventory + responses are empty there's nothing meaningful to
  // chart. Show one empty card instead of four duplicates.
  if (!hasResponses && !hasInventory) {
    return (
      <Card>
        <CardContent>
          <p className="py-12 text-center text-sm text-[var(--color-fg-muted)]">
            {t('stations.detail.dashboardEmpty')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>{t('stations.detail.responsesOverTime')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsesOverTimeChart data={summary.responses} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('stations.detail.inventoryDonut')}</CardTitle>
        </CardHeader>
        <CardContent>
          <InventoryDonut totals={summary.inventoryTotals} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('stations.detail.stockDistribution')}</CardTitle>
        </CardHeader>
        <CardContent>
          <StockWonChart inventory={summary.inventory} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('stations.detail.topAnswers')}</CardTitle>
        </CardHeader>
        <CardContent>
          <TopAnswersChart components={summary.responses.topAnswers} />
        </CardContent>
      </Card>
    </div>
  );
}
